<template>
  <div class="resolution-details-page">
    <div v-if="loading" class="text-center py-12">
      <v-progress-circular indeterminate color="primary" size="64" />
      <p class="text-h6 mt-4">Loading resolution...</p>
    </div>

    <div v-else-if="resolution">
      <!-- Page Header -->
      <div class="d-flex justify-space-between align-start mb-6">
        <div class="flex-grow-1">
          <div class="d-flex align-center mb-2">
            <v-btn
              icon
              variant="text"
              @click="$router.go(-1)"
              class="mr-2"
            >
              <v-icon>mdi-arrow-left</v-icon>
            </v-btn>
            <v-chip
              :color="getStatusColor(resolution.status)"
              variant="flat"
              class="mr-3"
            >
              {{ resolution.status?.toUpperCase() }}
            </v-chip>
            <v-chip
              v-if="resolution.priority && resolution.priority !== 'Medium'"
              :color="getPriorityColor(resolution.priority)"
              variant="flat"
              class="mr-3"
              size="small"
            >
              {{ resolution.priority?.toUpperCase() }}
            </v-chip>
            <v-chip
              v-if="resolution.is_confidential"
              color="warning"
              variant="flat"
            >
              CONFIDENTIAL
            </v-chip>
          </div>
          
          <h1 class="text-h4 font-weight-bold text-primary mb-2">
            {{ resolution.title }}
          </h1>
          
          <div class="d-flex align-center text-medium-emphasis mb-4">
            <v-icon size="small" class="mr-2">mdi-account</v-icon>
            <span class="mr-4">Proposed by {{ resolution.creator?.full_name || 'Unknown' }}</span>
            <v-icon size="small" class="mr-2">mdi-calendar</v-icon>
            <span class="mr-4">{{ formatDate(resolution.created_at) }}</span>
            <v-icon size="small" class="mr-2">mdi-shape</v-icon>
            <span>{{ resolution.type }}</span>
          </div>
        </div>

        <div class="d-flex align-center gap-2">
          <v-btn
            v-if="canEdit"
            color="primary"
            variant="outlined"
            @click="editResolution"
            prepend-icon="mdi-pencil"
          >
            Edit
          </v-btn>
          
          <v-btn
            color="secondary"
            variant="outlined"
            @click="shareResolution"
            prepend-icon="mdi-share"
          >
            Share
          </v-btn>

          <v-menu>
            <template #activator="{ props }">
              <v-btn
                icon
                variant="text"
                v-bind="props"
              >
                <v-icon>mdi-dots-vertical</v-icon>
              </v-btn>
            </template>
            <v-list density="compact">
              <v-list-item @click="duplicateResolution">
                <v-list-item-title>Duplicate</v-list-item-title>
                <template #prepend>
                  <v-icon>mdi-content-copy</v-icon>
                </template>
              </v-list-item>
              <v-list-item @click="printResolution">
                <v-list-item-title>Print</v-list-item-title>
                <template #prepend>
                  <v-icon>mdi-printer</v-icon>
                </template>
              </v-list-item>
              <v-list-item @click="exportResolution">
                <v-list-item-title>Export PDF</v-list-item-title>
                <template #prepend>
                  <v-icon>mdi-file-pdf-box</v-icon>
                </template>
              </v-list-item>
              <v-divider />
              <v-list-item v-if="canDelete" @click="deleteResolution" class="text-error">
                <v-list-item-title>Delete</v-list-item-title>
                <template #prepend>
                  <v-icon>mdi-delete</v-icon>
                </template>
              </v-list-item>
            </v-list>
          </v-menu>
        </div>
      </div>

      <v-row>
        <!-- Main Content -->
        <v-col cols="12" md="8">
          <!-- Resolution Details -->
          <v-card class="mb-6">
            <v-card-title class="text-h6 font-weight-medium">
              Resolution Description
            </v-card-title>
            <v-card-text>
              <div class="resolution-content">
                <div class="text-body-1 mb-4" style="white-space: pre-wrap;">{{ resolution.description }}</div>
                
                <v-divider class="my-4" />
                
                <h4 class="text-h6 font-weight-medium mb-3">Proposed Action</h4>
                <div class="text-body-1 mb-4" style="white-space: pre-wrap;">{{ resolution.proposed_action }}</div>
                
                <div v-if="resolution.rationale">
                  <v-divider class="my-4" />
                  <h4 class="text-h6 font-weight-medium mb-3">Rationale & Justification</h4>
                  <div class="text-body-1" style="white-space: pre-wrap;">{{ resolution.rationale }}</div>
                </div>
              </div>
            </v-card-text>
          </v-card>

          <!-- Financial Impact -->
          <v-card v-if="resolution.financial_impact" class="mb-6">
            <v-card-title class="text-h6 font-weight-medium">
              Financial Impact
            </v-card-title>
            <v-card-text>
              <v-row>
                <v-col cols="6">
                  <div class="d-flex align-center mb-3">
                    <v-icon class="mr-2" color="primary">mdi-currency-usd</v-icon>
                    <div>
                      <div class="text-caption text-medium-emphasis">Estimated Cost</div>
                      <div class="text-h6">${{ formatCurrency(resolution.financial_impact.estimated_cost) }}</div>
                    </div>
                  </div>
                </v-col>
                <v-col cols="6">
                  <div class="d-flex align-center mb-3">
                    <v-icon class="mr-2" color="primary">mdi-bank</v-icon>
                    <div>
                      <div class="text-caption text-medium-emphasis">Budget Source</div>
                      <div class="text-body-1">{{ resolution.financial_impact.budget_source }}</div>
                    </div>
                  </div>
                </v-col>
                <v-col cols="12" v-if="resolution.financial_impact.justification">
                  <div class="text-caption text-medium-emphasis">Financial Justification</div>
                  <div class="text-body-2">{{ resolution.financial_impact.justification }}</div>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>

          <!-- Voting Section -->
          <v-card v-if="isVotedStatus" class="mb-6">
            <v-card-title class="d-flex justify-space-between align-center">
              <span class="text-h6 font-weight-medium">Voting {{ (isClosed || isDeadlinePassed) ? 'Results' : '' }}</span>
              <v-chip
                :color="isDeadlinePassed && resolution.status === 'voting' ? 'error' : getVotingStatusColor(resolution.status)"
                variant="flat"
              >
                <v-icon v-if="isDeadlinePassed && resolution.status === 'voting'" start size="small">mdi-clock-alert</v-icon>
                {{ resolution.status === 'voting' ? (isDeadlinePassed ? 'CLOSED' : 'OPEN') : resolution.status?.toUpperCase() }}
              </v-chip>
            </v-card-title>

            <!-- Final result banner for closed/completed resolutions -->
            <div v-if="isClosed" class="px-4 pb-2">
              <v-alert
                :color="['passed', 'approved', 'implemented'].includes(resolution.status) ? 'success' : 'error'"
                variant="tonal"
                :icon="['passed', 'approved', 'implemented'].includes(resolution.status) ? 'mdi-check-circle' : 'mdi-close-circle'"
                density="comfortable"
              >
                <div class="text-subtitle-2 font-weight-bold mb-1">
                  Final Result: {{ resolution.status ? resolution.status.charAt(0).toUpperCase() + resolution.status.slice(1) : '' }}
                </div>
                <div class="text-body-2">
                  {{ resolution.vote_summary?.approve || 0 }} for
                  &nbsp;·&nbsp; {{ resolution.vote_summary?.reject || 0 }} against
                  &nbsp;·&nbsp; {{ resolution.vote_summary?.abstain || 0 }} abstained
                  &nbsp;·&nbsp; {{ resolution.vote_summary?.total || 0 }} total votes cast
                </div>
              </v-alert>
            </div>

            <v-card-text>
              <!-- Voting Progress -->
              <div class="voting-progress mb-4">
                <div class="d-flex justify-space-between align-center mb-2">
                  <span class="text-subtitle-2">Voting Progress</span>
                  <span class="text-body-2">
                    {{ resolution.vote_summary?.total || 0 }} / {{ 7 }} votes cast
                  </span>
                </div>
                
                <v-progress-linear
                  :model-value="getVotingProgress()"
                  color="primary"
                  height="8"
                  rounded
                  class="mb-2"
                />

                <div class="d-flex justify-space-between align-center text-caption text-medium-emphasis">
                  <span>{{ getVotingProgress().toFixed(1) }}% participation</span>
                  <span v-if="resolution.voting_deadline" class="d-flex align-center gap-1">
                    <v-chip
                      v-if="isDeadlinePassed && resolution.status === 'voting'"
                      color="error"
                      size="x-small"
                      variant="flat"
                      prepend-icon="mdi-clock-alert"
                    >
                      Deadline Expired
                    </v-chip>
                    <span v-else>Deadline: {{ formatDateTime(resolution.voting_deadline) }}</span>
                  </span>
                </div>
              </div>

              <!-- Vote Breakdown -->
              <v-row class="vote-breakdown mb-4">
                <v-col cols="4">
                  <v-card color="success" variant="tonal" class="text-center pa-3">
                    <div class="text-h4">{{ resolution.vote_summary?.approve || 0 }}</div>
                    <div class="text-caption">Votes For</div>
                    <div class="text-caption">
                      {{ getVotePercentage('approve') }}%
                    </div>
                  </v-card>
                </v-col>
                <v-col cols="4">
                  <v-card color="error" variant="tonal" class="text-center pa-3">
                    <div class="text-h4">{{ resolution.vote_summary?.reject || 0 }}</div>
                    <div class="text-caption">Votes Against</div>
                    <div class="text-caption">
                      {{ getVotePercentage('reject') }}%
                    </div>
                  </v-card>
                </v-col>
                <v-col cols="4">
                  <v-card color="warning" variant="tonal" class="text-center pa-3">
                    <div class="text-h4">{{ resolution.vote_summary?.abstain || 0 }}</div>
                    <div class="text-caption">Abstentions</div>
                    <div class="text-caption">
                      {{ getVotePercentage('abstain') }}%
                    </div>
                  </v-card>
                </v-col>
              </v-row>

              <!-- Voting Requirements -->
              <div class="voting-requirements mb-4">
                <h4 class="text-subtitle-1 mb-2">Voting Requirements</h4>
                <v-row dense>
                  <v-col cols="6">
                    <div class="d-flex align-center">
                      <v-icon size="small" class="mr-2">mdi-account-group</v-icon>
                      <span class="text-body-2">Quorum: {{ resolution.quorum_required }}%</span>
                    </div>
                  </v-col>
                  <v-col cols="6">
                    <div class="d-flex align-center">
                      <v-icon size="small" class="mr-2">mdi-chart-pie</v-icon>
                      <span class="text-body-2">Majority: {{ resolution.majority_required }}%</span>
                    </div>
                  </v-col>
                </v-row>
              </div>

              <!-- User's Vote Section -->
              <div v-if="isAuthenticated" class="user-vote-section">
                <v-divider class="mb-4" />
                
                <div v-if="userVote" class="user-voted">
                  <v-alert
                    :color="getVoteColor(userVote.vote_value)"
                    variant="tonal"
                    class="mb-3"
                  >
                    <template #prepend>
                      <v-icon>mdi-check-circle</v-icon>
                    </template>
                    <div>
                      <strong>You voted: {{ getVoteDisplayText(userVote.vote_value) }}</strong>
                      <div v-if="userVote.comments" class="text-body-2 mt-1">
                        "{{ userVote.comments }}"
                      </div>
                      <div class="text-caption mt-1">
                        Voted on {{ formatDateTime(userVote.voted_at) }}
                      </div>
                      <div class="text-caption text-success mt-1">
                        ✓ Your vote has been recorded. You cannot vote again on this resolution.
                      </div>
                    </div>
                  </v-alert>
                </div>
                
                <div v-else-if="canVote" class="voting-actions">
                  <h4 class="text-subtitle-1 mb-3">Cast Your Vote</h4>
                  <div class="d-flex gap-2 mb-3">
                    <v-btn
                      color="success"
                      variant="flat"
                      @click="castVote('for')"
                      :loading="votingLoading"
                    >
                      <v-icon class="mr-2">mdi-thumb-up</v-icon>
                      Vote For
                    </v-btn>
                    <v-btn
                      color="error"
                      variant="flat"
                      @click="castVote('against')"
                      :loading="votingLoading"
                    >
                      <v-icon class="mr-2">mdi-thumb-down</v-icon>
                      Vote Against
                    </v-btn>
                    <v-btn
                      color="warning"
                      variant="flat"
                      @click="castVote('abstain')"
                      :loading="votingLoading"
                    >
                      <v-icon class="mr-2">mdi-minus</v-icon>
                      Abstain
                    </v-btn>
                  </div>
                </div>

                <!-- Deadline passed but status not yet updated -->
                <div v-else-if="resolution.status === 'voting' && isDeadlinePassed" class="deadline-passed">
                  <v-alert
                    type="warning"
                    variant="tonal"
                    icon="mdi-clock-alert"
                    class="mb-3"
                  >
                    <div class="d-flex justify-space-between align-center flex-wrap gap-2">
                      <div>
                        <strong>Voting period has ended</strong>
                        <div class="text-body-2 mt-1">
                          The deadline passed on {{ formatDateTime(resolution.voting_deadline) }}.
                          Voting is now closed.
                        </div>
                      </div>
                      <v-btn
                        v-if="canEdit"
                        color="warning"
                        variant="flat"
                        size="small"
                        :loading="closeVotingLoading"
                        @click="closeVoting(true)"
                        prepend-icon="mdi-gavel"
                      >
                        Finalize &amp; Close
                      </v-btn>
                    </div>
                  </v-alert>
                </div>

                <div v-else-if="resolution.status !== 'voting'" class="voting-closed">
                  <v-alert color="grey" variant="tonal" icon="mdi-lock">
                    Voting has closed for this resolution
                  </v-alert>
                </div>
              </div>

              <!-- Login Required -->
              <div v-else class="login-required">
                <v-alert color="info" variant="tonal">
                  <template #prepend>
                    <v-icon>mdi-login</v-icon>
                  </template>
                  Please log in to participate in voting
                  <template #append>
                    <v-btn
                      color="primary"
                      size="small"
                      variant="text"
                      @click="$router.push('/login')"
                    >
                      Login
                    </v-btn>
                  </template>
                </v-alert>
              </div>
            </v-card-text>
          </v-card>

          <!-- Supporting Documents -->
          <v-card v-if="resolution.documents && resolution.documents.length > 0" class="mb-6">
            <v-card-title class="text-h6 font-weight-medium">
              Supporting Documents
            </v-card-title>
            <v-card-text>
              <div class="document-grid">
                <div
                  v-for="document in resolution.documents"
                  :key="document.id"
                  class="document-item d-flex align-center pa-3 mb-2"
                  @click="viewDocument(document)"
                >
                  <v-icon
                    :color="getDocumentColor(document.type)"
                    size="32"
                    class="mr-3"
                  >
                    {{ getDocumentIcon(document.type) }}
                  </v-icon>
                  <div class="flex-grow-1">
                    <div class="font-weight-medium">{{ document.title }}</div>
                    <div class="text-caption text-medium-emphasis">{{ document.type }}</div>
                    <div v-if="document.description" class="text-body-2 mt-1">{{ document.description }}</div>
                  </div>
                  <v-btn
                    icon
                    size="small"
                    variant="text"
                    @click.stop="downloadDocument(document)"
                  >
                    <v-icon>mdi-download</v-icon>
                  </v-btn>
                </div>
              </div>
            </v-card-text>
          </v-card>

          <!-- Implementation Notes -->
          <v-card v-if="(resolution.status === 'approved' || resolution.status === 'implemented') && resolution.implementation_notes" class="mb-6">
            <v-card-title class="text-h6 font-weight-medium">
              Implementation Notes
            </v-card-title>
            <v-card-text>
              <div class="implementation-notes">
                <p class="text-body-1">{{ resolution.implementation_notes }}</p>
              </div>
            </v-card-text>
          </v-card>
        </v-col>

        <!-- Sidebar -->
        <v-col cols="12" md="4">
          <!-- Quick Info -->
          <v-card class="mb-6">
            <v-card-title class="text-h6 font-weight-medium">
              Resolution Information
            </v-card-title>
            <v-card-text>
              <div class="info-items">
                <div class="info-item d-flex justify-space-between mb-3">
                  <span class="text-medium-emphasis">Type:</span>
                  <span class="font-weight-medium">{{ resolution.type }}</span>
                </div>
                <div class="info-item d-flex justify-space-between mb-3">
                  <span class="text-medium-emphasis">Priority:</span>
                  <v-chip
                    :color="getPriorityColor(resolution.priority)"
                    size="small"
                    variant="flat"
                  >
                    {{ resolution.priority }}
                  </v-chip>
                </div>
                <div v-if="resolution.meeting" class="info-item d-flex justify-space-between mb-3">
                  <span class="text-medium-emphasis">Meeting:</span>
                  <span class="font-weight-medium">{{ resolution.meeting.title }}</span>
                </div>
                <div v-if="resolution.agenda_item" class="info-item d-flex justify-space-between mb-3">
                  <span class="text-medium-emphasis">Agenda Item:</span>
                  <span class="font-weight-medium">{{ resolution.agenda_item }}</span>
                </div>
                <div class="info-item d-flex justify-space-between mb-3">
                  <span class="text-medium-emphasis">Created:</span>
                  <span>{{ formatDate(resolution.created_at) }}</span>
                </div>
                <div v-if="resolution.updated_at !== resolution.created_at" class="info-item d-flex justify-space-between">
                  <span class="text-medium-emphasis">Last Modified:</span>
                  <span>{{ formatDate(resolution.updated_at) }}</span>
                </div>
              </div>
            </v-card-text>
          </v-card>

          <!-- Tags -->
          <v-card v-if="resolution.tags && resolution.tags.length > 0" class="mb-6">
            <v-card-title class="text-h6 font-weight-medium">
              Tags
            </v-card-title>
            <v-card-text>
              <div class="d-flex flex-wrap gap-2">
                <v-chip
                  v-for="tag in resolution.tags"
                  :key="tag"
                  size="small"
                  variant="outlined"
                >
                  {{ tag }}
                </v-chip>
              </div>
            </v-card-text>
          </v-card>

          <!-- Voting Details / Summary -->
          <v-card v-if="isVotedStatus">
            <v-card-title class="text-h6 font-weight-medium">
              {{ isClosed ? 'Voting Summary' : 'Voting Configuration' }}
            </v-card-title>
            <v-card-text>
              <div class="voting-config">
                <!-- Final tally (closed resolutions only) -->
                <template v-if="isClosed">
                  <div class="config-item mb-3">
                    <div class="text-caption text-medium-emphasis">Votes For</div>
                    <div class="font-weight-bold text-success">{{ resolution.vote_summary?.approve || 0 }}</div>
                  </div>
                  <div class="config-item mb-3">
                    <div class="text-caption text-medium-emphasis">Votes Against</div>
                    <div class="font-weight-bold text-error">{{ resolution.vote_summary?.reject || 0 }}</div>
                  </div>
                  <div class="config-item mb-3">
                    <div class="text-caption text-medium-emphasis">Abstentions</div>
                    <div class="font-weight-bold text-warning">{{ resolution.vote_summary?.abstain || 0 }}</div>
                  </div>
                  <div class="config-item mb-3">
                    <div class="text-caption text-medium-emphasis">Total Votes Cast</div>
                    <div class="font-weight-medium">{{ resolution.vote_summary?.total || 0 }}</div>
                  </div>
                  <div class="config-item mb-3">
                    <div class="text-caption text-medium-emphasis">Participation</div>
                    <div class="font-weight-medium">{{ getVotingProgress().toFixed(1) }}%</div>
                  </div>
                  <v-divider class="mb-3" />
                </template>

                <div class="config-item mb-3">
                  <div class="text-caption text-medium-emphasis">Voting Type</div>
                  <div class="font-weight-medium">{{ resolution.vote_type || 'Simple Majority' }}</div>
                </div>
                <div class="config-item mb-3">
                  <div class="text-caption text-medium-emphasis">Quorum Required</div>
                  <div class="font-weight-medium">{{ resolution.quorum_required || 'Simple' }}%</div>
                </div>
                <div class="config-item mb-3">
                  <div class="text-caption text-medium-emphasis">Majority Required</div>
                  <div class="font-weight-medium">{{ resolution.majority_required || '50' }}%</div>
                </div>
                <div v-if="resolution.voting_deadline" class="config-item">
                  <div class="text-caption text-medium-emphasis">
                    {{ isClosed ? 'Voting Deadline' : 'Voting Deadline' }}
                  </div>
                  <div class="font-weight-medium">{{ formatDateTime(resolution.voting_deadline) }}</div>
                </div>
              </div>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </div>

    <!-- Vote Confirmation Dialog -->
    <v-dialog v-model="showVoteDialog" max-width="500">
      <v-card>
        <v-card-title>Confirm Your Vote</v-card-title>
        <v-card-text>
          <p class="text-body-1 mb-4">
            You are about to vote <strong>{{ voteType.toUpperCase() }}</strong> on this resolution.
          </p>
          
          <v-textarea
            v-model="voteComments"
            label="Comments (Optional)"
            variant="outlined"
            rows="3"
            hint="Provide any additional comments or reasoning for your vote"
            persistent-hint
          />
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn @click="showVoteDialog = false">Cancel</v-btn>
          <v-btn
            :color="getVoteColor(voteType)"
            :loading="confirmingVote"
            @click="confirmVote"
          >
            Confirm Vote
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Delete Confirmation Dialog -->
    <v-dialog v-model="showDeleteDialog" max-width="400">
      <v-card>
        <v-card-title class="text-h6">Delete Resolution</v-card-title>
        <v-card-text>
          Are you sure you want to delete this resolution? This action cannot be undone.
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn @click="showDeleteDialog = false">Cancel</v-btn>
          <v-btn
            color="error"
            :loading="deleting"
            @click="confirmDelete"
          >
            Delete
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { ref, computed, onMounted } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useToast } from 'vue-toastification'
import { useAuthStore } from '@/store/auth'
import { useBreadcrumb } from '@/composables/useBreadcrumb'
import api from '@/services/api'

export default {
  name: 'ResolutionDetails',
  setup() {
    const router = useRouter()
    const route = useRoute()
    const toast = useToast()
    const authStore = useAuthStore()

    // State
    const loading = ref(true)
    const resolution = ref(null)
    
    // Computed authentication state
    const isAuthenticated = computed(() => authStore.isAuthenticated)
    const currentUserId = computed(() => authStore.user?.id)
    const canEdit = computed(() => authStore.user?.is_admin || false)
    const canDelete = computed(() => authStore.user?.is_admin || false)
    const canChangeVote = ref(true) // TODO: Check if vote changes are allowed
    const { setBreadcrumbTitle } = useBreadcrumb()

    // Dialog states
    const showVoteDialog = ref(false)
    const showDeleteDialog = ref(false)
    const votingLoading = ref(false)
    const closeVotingLoading = ref(false)
    const confirmingVote = ref(false)
    const deleting = ref(false)
    const voteType = ref('')
    const voteComments = ref('')

    // Computed properties
    const userVote = computed(() => {
      if (!resolution.value?.votes || !currentUserId.value) return null
      return resolution.value.votes.find(vote => vote.user_id === currentUserId.value)
    })

    const canVote = computed(() => {
      return resolution.value?.status === 'voting' &&
             isAuthenticated.value &&
             !userVote.value &&
             !votingLoading.value &&
             !isDeadlinePassed.value
    })

    // True when the voting deadline exists and has passed
    const isDeadlinePassed = computed(() => {
      if (!resolution.value?.voting_deadline) return false
      return new Date() > new Date(resolution.value.voting_deadline)
    })

    // True for any status that went through (or is in) voting
    const isVotedStatus = computed(() => {
      return ['voting', 'passed', 'approved', 'rejected', 'implemented', 'archived'].includes(
        resolution.value?.status
      )
    })

    // True once voting has concluded (read-only results view)
    const isClosed = computed(() => {
      return ['passed', 'approved', 'rejected', 'implemented', 'archived'].includes(
        resolution.value?.status
      )
    })

    // Methods
    const formatDate = (dateString) => {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      })
    }

    const formatDateTime = (dateString) => {
      return new Date(dateString).toLocaleString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      })
    }

    const formatCurrency = (amount) => {
      return new Intl.NumberFormat('en-US').format(amount)
    }

    const getStatusColor = (status) => {
      const colors = {
        draft: 'grey',
        pending: 'warning',
        voting: 'info',
        passed: 'success',
        rejected: 'error',
        implemented: 'purple',
        archived: 'grey-darken-2'
      }
      return colors[status] || 'grey'
    }

    const getVotingStatusColor = (status) => {
      const colors = {
        'draft': 'grey',
        'voting': 'success',
        'approved': 'primary',
        'rejected': 'error'
      }
      return colors[status] || 'grey'
    }

    const getPriorityColor = (priority) => {
      const colors = {
        Low: 'success',
        Medium: 'info', 
        High: 'warning',
        Urgent: 'error'
      }
      return colors[priority] || 'grey'
    }

    const getVoteColor = (voteValue) => {
      const colors = {
        'approve': 'success',
        'reject': 'error',
        'abstain': 'warning',
        // Support legacy frontend values too
        'for': 'success',
        'against': 'error'
      }
      return colors[voteValue] || 'grey'
    }

    const getVoteDisplayText = (voteValue) => {
      const displayTexts = {
        'approve': 'FOR',
        'reject': 'AGAINST',
        'abstain': 'ABSTAIN'
      }
      return displayTexts[voteValue] || voteValue?.toUpperCase()
    }

    const getDocumentIcon = (type) => {
      const icons = {
        'Policy Document': 'mdi-file-document',
        'Financial Report': 'mdi-file-chart',
        'Legal Document': 'mdi-gavel',
        'Research Report': 'mdi-file-search',
        'Presentation': 'mdi-presentation',
        'Correspondence': 'mdi-email',
        'Supporting Evidence': 'mdi-file-check',
        'Other': 'mdi-file'
      }
      return icons[type] || 'mdi-file'
    }

    const getDocumentColor = (type) => {
      const colors = {
        'Policy Document': 'primary',
        'Financial Report': 'success',
        'Legal Document': 'warning',
        'Research Report': 'info',
        'Presentation': 'purple',
        'Correspondence': 'teal',
        'Supporting Evidence': 'indigo',
        'Other': 'grey'
      }
      return colors[type] || 'grey'
    }

    const getVotingProgress = () => {
      if (!resolution.value?.vote_summary?.total) return 0
      // For now, assume all board members can vote (this could be enhanced)
      const totalEligibleVoters = 7 // TODO: Get from API or compute based on active board members
      return (resolution.value.vote_summary.total / totalEligibleVoters) * 100
    }

    const getVotePercentage = (type) => {
      if (!resolution.value?.vote_summary?.total) return 0
      const votes = resolution.value.vote_summary[type] || 0
      return ((votes / resolution.value.vote_summary.total) * 100).toFixed(1)
    }

    const castVote = (type) => {
      voteType.value = type
      voteComments.value = ''
      showVoteDialog.value = true
    }

    const confirmVote = async () => {
      confirmingVote.value = true
      try {
        // Map frontend vote types to backend values
        const voteValueMap = {
          'for': 'approve',
          'against': 'reject',
          'abstain': 'abstain'
        }
        
        const response = await api.post(`/resolutions/${resolution.value.id}/vote`, {
          vote_value: voteValueMap[voteType.value],
          comments: voteComments.value
        })

        // Update local data
        if (!resolution.value.votes) resolution.value.votes = []
        
        // Remove any existing vote from this user and add the new one
        resolution.value.votes = resolution.value.votes.filter(vote => vote.user_id !== currentUserId.value)
        resolution.value.votes.push(response.data.vote)
        
        // Update vote summary from backend response
        if (response.data.vote_summary) {
          resolution.value.vote_summary = response.data.vote_summary
        }

        toast.success('Vote cast successfully')
        showVoteDialog.value = false
      } catch (error) {
        console.error('Error casting vote:', error)
        const message = error.response?.data?.message || 'Failed to cast vote'
        toast.error(message)
      } finally {
        confirmingVote.value = false
      }
    }

    const changeVote = () => {
      // Allow user to change their vote
      toast.info('Vote change functionality coming soon')
    }

    const closeVoting = async (force = false) => {
      closeVotingLoading.value = true
      try {
        const response = await api.post(`/resolutions/${resolution.value.id}/close-voting`, { force })
        resolution.value = response.data.resolution
        if (response.data.vote_summary) {
          resolution.value.vote_summary = response.data.vote_summary
        }
        toast.success(response.data.message)
      } catch (error) {
        const msg = error.response?.data?.message || 'Failed to close voting'
        toast.error(msg)
      } finally {
        closeVotingLoading.value = false
      }
    }

    const editResolution = () => {
      router.push(`/resolutions/${resolution.value.id}/edit`)
    }

    const duplicateResolution = () => {
      router.push({
        path: '/resolutions/create',
        query: { duplicate: resolution.value.id }
      })
    }

    const shareResolution = () => {
      navigator.clipboard.writeText(window.location.href)
      toast.success('Resolution link copied to clipboard')
    }

    const printResolution = () => {
      window.print()
    }

    const exportResolution = () => {
      // Implement PDF export
      toast.info('PDF export functionality coming soon')
    }

    const deleteResolution = () => {
      showDeleteDialog.value = true
    }

    const confirmDelete = async () => {
      deleting.value = true
      try {
        await api.delete(`/resolutions/${resolution.value.id}`)
        toast.success('Resolution deleted successfully')
        router.push('/resolutions')
      } catch (error) {
        console.error('Error deleting resolution:', error)
        toast.error('Failed to delete resolution')
      } finally {
        deleting.value = false
      }
    }

    const viewDocument = (document) => {
      // Open document viewer
      router.push(`/documents/${document.id}`)
    }

    const downloadDocument = async (document) => {
      try {
        const response = await api.get(`/documents/${document.id}/download`, {
          responseType: 'blob'
        })
        
        const url = window.URL.createObjectURL(new Blob([response.data]))
        const link = document.createElement('a')
        link.href = url
        link.setAttribute('download', document.title)
        document.body.appendChild(link)
        link.click()
        link.remove()
        window.URL.revokeObjectURL(url)
      } catch (error) {
        console.error('Error downloading document:', error)
        toast.error('Failed to download document')
      }
    }

    // Data loading
    const loadResolution = async () => {
      loading.value = true
      try {
        const response = await api.get(`/resolutions/${route.params.id}`)
        resolution.value = response.data.resolution
        setBreadcrumbTitle(resolution.value.title)
        
        // Load implementation data if the resolution is passed
        // For now, we'll assume all passed resolutions may have implementation notes
        if (resolution.value.status === 'approved' || resolution.value.status === 'implemented') {
          // Implementation data is already included in the resolution response
          // via the implementation_notes field
        }
      } catch (error) {
        console.error('Error loading resolution:', error)
        toast.error('Failed to load resolution')
        router.push('/resolutions')
      } finally {
        loading.value = false
      }
    }

    // Lifecycle
    onMounted(() => {
      loadResolution()
    })

    return {
      // State
      loading,
      resolution,
      isAuthenticated,
      currentUserId,
      canEdit,
      canDelete,
      canChangeVote,

      // Dialog states
      showVoteDialog,
      showDeleteDialog,
      votingLoading,
      closeVotingLoading,
      confirmingVote,
      deleting,
      voteType,
      voteComments,

      // Computed
      userVote,
      canVote,
      isDeadlinePassed,
      isVotedStatus,
      isClosed,

      // Methods
      formatDate,
      formatDateTime,
      formatCurrency,
      getStatusColor,
      getVotingStatusColor,
      getPriorityColor,
      getVoteColor,
      getVoteDisplayText,
      getDocumentIcon,
      getDocumentColor,
      getVotingProgress,
      getVotePercentage,
      castVote,
      confirmVote,
      changeVote,
      closeVoting,
      editResolution,
      duplicateResolution,
      shareResolution,
      printResolution,
      exportResolution,
      deleteResolution,
      confirmDelete,
      viewDocument,
      downloadDocument
    }
  }
}
</script>

<style scoped lang="scss">
@import "@/styles/variables.scss";

.resolution-details-page {
  .text-medium-emphasis {
    opacity: 0.7;
  }

  .text-primary {
    color: rgb(var(--v-theme-primary)) !important;
  }

  .resolution-content {
    line-height: 1.6;
  }

  .vote-breakdown {
    .v-card {
      transition: transform 0.2s;

      &:hover {
        transform: scale(1.02);
      }
    }
  }

  .document-item {
    border: 1px solid rgba(0, 0, 0, 0.12);
    border-radius: 8px;
    cursor: pointer;
    transition: background-color 0.2s;

    &:hover {
      background-color: rgba(var(--v-theme-primary), 0.04);
    }
  }

  .info-items,
  .voting-config {
    .info-item,
    .config-item {
      padding-bottom: 8px;
      border-bottom: 1px solid rgba(0, 0, 0, 0.06);

      &:last-child {
        border-bottom: none;
        padding-bottom: 0;
      }
    }
  }

  // Print styles
  @media print {
    .v-btn,
    .resolution-actions {
      display: none !important;
    }
  }
}
</style>